<script lang="ts" setup>
import { onUnmounted, ref } from 'vue'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import MorphSVGPlugin from 'gsap/MorphSVGPlugin'
import { CircleEffectAtionName } from './CircleEffectAtionName'
import { store } from "@/store"
import { watchImmediate } from '@vueuse/core'

if (import.meta.client)
  gsap.registerPlugin(ScrollTrigger, MorphSVGPlugin)

const { $bus } = useNuxtApp()
const { xs } = useLayoutMode()
const { theme } = useTheme()
const appReady = useAppReady()
const route = useRoute()
const contentRef = ref<HTMLElement | null>(null)
const moveEffectRef = ref<HTMLElement | null>(null)
const adavanceRender = ref(false)
const isAboutPage = computed(() => route.name === 'about')
const svgStopConfigs = [
  {
    selector: '.circle-move-effect .stop1',
    colors: ['#704A50', '#355EF0', '#129839']
  },
  {
    selector: '.circle-move-effect .stop2',
    colors: ['#351E24', '#016DD2', '#018972']
  },
  {
    selector: '.circle-move-effect .stop3',
    colors: ['#62000A', '#1D10E3', '#19A23A']
  },
  {
    selector: '.circle-move-effect .stop4',
    colors: ['#2F151E', '#1D10E3', '#008D72']
  },
  {
    selector: '.circle-move-effect .stop2_1',
    colors: ['#CB938C', '#355EF0', '#129839']
  },
  {
    selector: '.circle-move-effect .stop2_2',
    colors: ['#943D45', '#016DD2', '#018972']
  },
  {
    selector: '.circle-move-effect .stop2_3',
    colors: ['#B92E3D', '#1D10E3', '#19A23A']
  },
  {
    selector: '.circle-move-effect .stop2_4',
    colors: ['#DABCBA', '#1D10E3', '#008D72']
  }
]

let ctx: gsap.Context

function initHomePageEffect() {
  const targetStyle = moveEffectRef.value!.style

  ctx = gsap.context(() => {
    gsap.to(contentRef.value, { autoAlpha: 1 })
    const seting = gsap.quickSetter(contentRef.value, 'opacity')
    gsap.timeline()
      .to(contentRef.value, {
        immediateRender: false,
        xPercent: () => xs.value ? 70 : 100,
        yPercent: () => xs.value ? 110 : 40,
        ease: 'none',
        scrollTrigger: {
          trigger: 'body',
          start: 'top top',
          endTrigger: '.home-page .page2',
          scrub: true,
          onUpdate: (self: any) => {
            seting(1 - self.progress)
          },
          onLeave: () => {
            gsap.set(contentRef.value, {
              xPercent: () => {
                if (xs.value) return -70
                else return -100
              },
            })
          },
          onEnterBack: () => {
            gsap.set(contentRef.value, {
              xPercent: () => {
                if (xs.value) return 70
                else return 120
              },
            })
          },
          end: 'top top',
        }
      }).to(contentRef.value, {
        immediateRender: false,
        xPercent: () => xs.value ? -55 : -35,
        yPercent: () => xs.value ? -7 : -10,
        scale: () => xs.value ? 1 : 0.907,
        ease: 'none',
        scrollTrigger: {
          trigger: '.home-page .page2',
          start: xs.value? 'bottom+=20% bottom' :'bottom bottom',
          endTrigger: '.home-page .page3',
          scrub: true,
          onUpdate: (self: any) => {
            seting(self.progress)
          },
          end: () => xs.value ? 'top 1rem' : 'top 26%',
        }
      })
      // .to(contentRef.value, {
      //   immediateRender: false,
      //   xPercent: () => {
      //     if (xs.value) return -78
      //     else return -42
      //   },
      //   yPercent: () => {
      //     if (xs.value) return -22
      //     else return -10
      //   },
      //   scale: () => {
      //     if (xs.value) return 1
      //     else return 0.907
      //   },
      //   ease: 'none',
      //   scrollTrigger: {
      //     trigger: 'body',
      //     start: 'top top',
      //     endTrigger: '.home-page .page3',
      //     end: 'top top',
      //     scrub: xs.value ? 0.3 : true,
      //   }
      // })
      .to(contentRef.value, {
        xPercent: xs.value ? 56 : 66,
        ease: 'none',
        scale: 1.22,
        immediateRender: false,
        scrollTrigger: {
          trigger: '.home-page .page3',
          start: 'bottom bottom',
          endTrigger: '.home-page .page4 .item-box',
          end: () => {
            if (xs.value) return 'top center+=11%'
            else return 'center center'
          },
          scrub: xs.value ? 0.3 : true,
        }
      }).to(contentRef.value, {
        xPercent: 0,
        yPercent: () => xs.value ? 13 : -132,
        ease: 'none',
        scale: xs.value ? 1 : 3,
        immediateRender: false,
        scrollTrigger: {
          trigger: '.home-page .page5 .page5-content',
          start: 'top bottom',
          endTrigger: '.home-page .page5 .page5-content',
          end: 'top center',
          scrub: true,
          onLeave() {
            targetStyle.position = 'absolute'
            targetStyle.top = document.documentElement.scrollTop + 'px'
          },
          onEnterBack() {
            targetStyle.position = 'fixed'
            targetStyle.top = ''
          }
        }
      })
  })
}

function initAboutPageEffct() {
  ctx = gsap.context(() => {
    gsap.to(contentRef.value, { autoAlpha: 1 })
    gsap.timeline()
      .to(contentRef.value, {
        immediateRender: false,
        xPercent: () => {
          if (xs.value) return -40
          else return -84
        },
        scale: xs.value ? 1.5 : 2.65,
        ease: 'none',
        scrollTrigger: {
          trigger: '.about-page .business',
          start: 'bottom bottom',
          end: 'center center',
          endTrigger: '.about-page .img_wrap_1',
          scrub: true,
        }
      }).to(contentRef.value, {
        xPercent: () => {
          if (xs.value) return 7
          else return 50
        },
        yPercent: xs.value ? -2 : -20,
        scale: xs.value ? 1 : 1.5456,

        ease: 'none',
        immediateRender: false,
        scrollTrigger: {
          trigger: '.about-page .img_wrap_1',
          start: 'center center',
          endTrigger: '.about-page .about_text_list',
          end: () => {
            if (xs.value) return 'bottom-=18% center'
            else return 'center center'
          },
          scrub: true,
        }
      }).to(contentRef.value, {
        yPercent: () => {
          if (xs.value) return -170
          else return -200
        },
        ease: 'none',
        immediateRender: false,
        scrollTrigger: {
          trigger: '.about-page .about_text_list',
          start: () => {
            if (xs.value) return 'bottom-=18% center'
            else return 'center center'
          },
          end: 'bottom+=300 top',
          endTrigger: '.about-page .about_text_list',
          scrub: true
        }
      })
  })
}

function initServicePageEffecct() {
  ctx = gsap.context(() => {
    gsap.to(contentRef.value, { autoAlpha: 1 })
    const triggerConfig1 = {
      trigger: 'body',
      start: 'top top',
      endTrigger: '.service-page .page-wrap1',
      end: xs.value ? 'top+=20% top+=30%' : 'top+=15% top+=40%',
      scrub: true,
    }
    const triggerConfig2 = {
      trigger: '.service-page .page-wrap1',
      start: xs.value ? 'top+=20% top+=30%' : 'top+=15% top+=40%',
      endTrigger: '.service-page .page-wrap2',
      end: xs.value ? 'top bottom+25rem' : 'top bottom+25rem',
      scrub: true,


    }
    const triggerConfig3 = {
      trigger: '.service-page .page-wrap2',
      start: xs.value ? 'top bottom+25rem' : 'top bottom+25rem',
      scrub: true
    }
    const tl = gsap.timeline()
    tl
      .addLabel('step1')
      .to(contentRef.value, {
        immediateRender: false,
        xPercent: () => {
          return xs.value ? -42 : -67
        },
        yPercent: () => {
          return xs.value ? -26 : -43
        },
        scale: () => {
          return xs.value ? 1 : 1.09
        },
        ease: 'none',
        scrollTrigger: triggerConfig1
      })
      .to('.circle-move-effect .circle', {
        fill: '#0146bd',
        scrollTrigger: triggerConfig1
      }, 'step1')
      .addLabel('step2')
      .to(contentRef.value, {
        immediateRender: false,
        xPercent: () => {
          return xs.value ? -4 : 6
        },
        yPercent: () => {
          return xs.value ? 74 : -33
        },
        scale: () => {
          return xs.value ? 1 : 0.9458
        },
        ease: 'none',
        scrollTrigger: triggerConfig2
      })
      .to(contentRef.value, {
        immediateRender: false,
        yPercent: () => {
          return xs.value ? -140 : -140
        },
        ease: 'none',
        scrollTrigger: triggerConfig3
      })
      .to('.circle-move-effect .circle', {
        immediateRender: false,
        fill: '#3db333',
        scrollTrigger: triggerConfig2
      }, 'step2')


    if (adavanceRender.value) {
      tl.to(svgStopConfigs.map(v => v.selector), {
        stopColor: (index) => svgStopConfigs[Math.floor(index / 2)].colors[1],
        scrollTrigger: triggerConfig1
      }, 'step1')
      tl.to(svgStopConfigs.map(v => v.selector), {
        immediateRender: false,
        stopColor: (index) => svgStopConfigs[Math.floor(index / 2)].colors[2],
        scrollTrigger: triggerConfig2
      }, 'step2')
    }
  })
}


onMounted(() => {
  adavanceRender.value = !isMobile()

  $bus.on(CircleEffectAtionName.Clear, () => {
    reset()
  })
  $bus.on(CircleEffectAtionName.Init, data => {
    console.log('page route', data)
    switch (data) {
      case 'index':
        initHomePageEffect()
        break;
      case 'about':
        initAboutPageEffct()
        break
      case 'service':
        initServicePageEffecct()
        break
    }
  })
  // 首页圆环换色
  $bus.on(CircleEffectAtionName.ChangeCorlor, index => {
    const duration = 0.5
    const tl = gsap.timeline()

    tl.to('.circle-move-effect .circle', {
      overwrite: true,
      fill: ['#B11425', '#0146bd', '#3db333'][<number>index],
      duration
    })

    if (adavanceRender.value) {
      tl.to(svgStopConfigs.map(v => v.selector), {
        overwrite: true,
        stopColor: (idx) => svgStopConfigs[Math.floor(idx / 2)].colors[<number>index],
        duration
      }, 0)
    }
  })

  // about页面圆环换形状
  $bus.on(CircleEffectAtionName.ChangeShape, index => {
    gsap.to('.circle-move-effect .circle', {
      overwrite: true,
      morphSVG: `#role_${Number(index) + 1}`,
      duration: 0.3
    })
  })

  watchImmediate(appReady, () => {
    if (!appReady.value)
      return;

    gsap.to('.circle-move-effect .content', {
      scale: 1,
      y: 0,
      duration: 0.3,
      ease: 'none'
    });
  })

  watch(xs, () => {
    ScrollTrigger.refresh(true)
  })

  watch(theme, () => {
    ScrollTrigger.refresh()
  })
})

function reset() {
  ctx?.revert()
  if (moveEffectRef.value) {
    moveEffectRef.value.style.position = 'fixed'
    moveEffectRef.value.style.top = ''
    gsap.set('.circle-move-effect .circle', {
      fill: '#B11425'
    })
    gsap.set(svgStopConfigs.map(v => v.selector), {
      stopColor: (idx) => svgStopConfigs[Math.floor(idx / 2)].colors[0],
    })
  }
  if (contentRef.value) {
    gsap.set(contentRef.value, {
      clearProps: 'all'
    })
  }
  gsap.set('.circle-move-effect .circle', {
    morphSVG: `#role_0`,
  })
}

onUnmounted(() => {
  reset()
})

</script>

<template>
  <div class="circle-move-effect" ref="moveEffectRef" :style="{ '--centerHeight': store.centerHeight + 'px' }">
    <div class="content-wrapper" ref="contentRef">
      <div class="content">
        <svg width="1318" height="1318" viewBox="0 0 1318 1318" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="circle" fill-rule="evenodd" clip-rule="evenodd"
            d="M658.987 2.53779e-07C745.528 -0.00240136 831.223 17.0409 911.178 50.1572C991.133 83.2736 1063.78 131.815 1124.98 193.008C1186.17 254.201 1234.71 326.849 1267.83 406.803C1300.95 486.756 1318 572.451 1318 658.992C1318 789.33 1279.35 916.741 1206.94 1025.11C1134.53 1133.49 1031.61 1217.95 911.194 1267.83C790.778 1317.71 658.276 1330.76 530.443 1305.34C402.61 1279.91 285.187 1217.15 193.023 1124.99C100.859 1032.83 38.094 915.405 12.6645 787.572C-12.7648 659.74 0.283843 527.238 50.1596 406.821C100.036 286.404 184.499 183.481 292.87 111.067C401.24 38.6541 528.65 0.00243245 658.987 2.53779e-07ZM841.376 218.679C754.29 182.607 658.461 173.17 566.011 191.56C473.561 209.949 388.641 255.34 321.988 321.993C255.335 388.646 209.943 473.567 191.553 566.018C173.163 658.468 182.602 754.295 218.673 841.382C254.745 928.468 315.83 1002.9 394.206 1055.27C472.581 1107.64 564.725 1135.6 658.987 1135.6C721.576 1135.6 783.552 1123.27 841.377 1099.32C899.203 1075.37 951.744 1040.26 996.001 996.008C1040.26 951.751 1075.37 899.209 1099.32 841.384C1123.27 783.558 1135.59 721.581 1135.59 658.992C1135.59 564.731 1107.64 472.587 1055.27 394.212C1002.9 315.837 928.463 254.751 841.376 218.679ZM1317.97 2.53779e-07V300.161C1248.75 173.415 1144.56 69.2272 1017.81 2.53779e-07H1317.97Z"
            fill="#B11425" />

          <g v-if="isAboutPage" style="display: none;">
            <path id="role_0"
              d="M658.987 2.53779e-07C745.528 -0.00240136 831.223 17.0409 911.178 50.1572C991.133 83.2736 1063.78 131.815 1124.98 193.008C1186.17 254.201 1234.71 326.849 1267.83 406.803C1300.95 486.756 1318 572.451 1318 658.992C1318 789.33 1279.35 916.741 1206.94 1025.11C1134.53 1133.49 1031.61 1217.95 911.194 1267.83C790.778 1317.71 658.276 1330.76 530.443 1305.34C402.61 1279.91 285.187 1217.15 193.023 1124.99C100.859 1032.83 38.094 915.405 12.6645 787.572C-12.7648 659.74 0.283843 527.238 50.1596 406.821C100.036 286.404 184.499 183.481 292.87 111.067C401.24 38.6541 528.65 0.00243245 658.987 2.53779e-07ZM841.376 218.679C754.29 182.607 658.461 173.17 566.011 191.56C473.561 209.949 388.641 255.34 321.988 321.993C255.335 388.646 209.943 473.567 191.553 566.018C173.163 658.468 182.602 754.295 218.673 841.382C254.745 928.468 315.83 1002.9 394.206 1055.27C472.581 1107.64 564.725 1135.6 658.987 1135.6C721.576 1135.6 783.552 1123.27 841.377 1099.32C899.203 1075.37 951.744 1040.26 996.001 996.008C1040.26 951.751 1075.37 899.209 1099.32 841.384C1123.27 783.558 1135.59 721.581 1135.59 658.992C1135.59 564.731 1107.64 472.587 1055.27 394.212C1002.9 315.837 928.463 254.751 841.376 218.679ZM1317.97 2.53779e-07V300.161C1248.75 173.415 1144.56 69.2272 1017.81 2.53779e-07H1317.97Z" />
            <path id="role_1"
              d="M713.187 284.543C790.387 284.543 854.617 348.783 854.617 425.973V664.903C854.617 681.523 849.267 697.863 836.307 710.823L834.337 712.793L832.357 714.763C826.727 720.113 821.377 723.783 815.737 725.753L790.097 736.743L801.087 1172.043C801.087 1184.723 797.417 1195.703 788.127 1205.003C779.107 1214.013 779.107 1219.653 755.167 1219.653C731.217 1219.653 711.217 1203.313 709.247 1179.363L661.347 852.293L613.447 1181.053C609.787 1203.033 591.467 1221.343 567.527 1221.343C554.847 1221.343 543.857 1215.993 534.557 1206.693C525.267 1197.683 521.607 1186.693 521.607 1173.733L532.597 738.433L506.957 727.443L490.337 716.453L486.677 712.793C475.687 699.833 468.367 683.493 468.367 666.873V427.953C468.367 348.783 532.877 284.543 611.767 284.543H713.187ZM1048.177 391.323C1118.047 391.323 1174.967 448.233 1174.967 518.103V729.413C1174.967 755.333 1158.337 777.313 1132.697 784.633L1118.047 788.293L1127.347 1177.673C1129.317 1188.663 1123.687 1199.653 1116.357 1206.973C1109.027 1216.273 1107.057 1219.923 1087.067 1219.923C1067.077 1219.923 1048.757 1203.593 1046.787 1183.303L1000.867 911.453L958.597 1183.303C956.907 1203.313 938.587 1219.923 918.297 1219.923C907.317 1219.923 896.327 1214.293 888.997 1206.973C881.677 1199.653 878.017 1188.663 878.017 1177.673L887.307 804.643L889.287 764.353C889.287 764.093 891.257 688.813 891.257 666.863V411.603C911.247 398.643 935.197 391.323 959.137 391.323H1048.177ZM362.147 391.043C385.807 391.043 409.757 398.363 430.037 411.333V666.583C430.037 688.803 432.007 763.753 432.017 764.063H431.447V804.353L440.747 1177.383C442.717 1188.373 437.087 1199.363 429.757 1206.683C422.437 1215.983 420.457 1219.643 400.457 1219.643C380.457 1219.643 362.147 1203.303 360.177 1183.023L315.937 911.173L273.677 1183.023C271.987 1205.003 253.677 1219.643 233.387 1219.643C222.397 1219.643 211.407 1214.013 204.087 1206.683C196.757 1199.363 193.097 1188.373 193.097 1177.383L204.087 788.013L189.437 784.353C165.497 778.713 147.177 754.773 147.177 729.133V517.823C147.177 447.953 204.087 391.043 271.987 391.043H362.147ZM317.907 178.043C369.457 178.043 409.757 218.333 409.757 269.893C409.757 321.443 369.457 361.743 317.907 361.743C266.357 361.743 226.067 321.443 226.067 269.893C226.067 218.343 266.357 178.043 317.907 178.043ZM1005.077 178.043C1056.627 178.043 1096.927 218.333 1096.927 269.893C1096.927 321.443 1056.627 361.743 1005.077 361.743C953.527 361.743 913.237 321.443 913.237 269.893C913.237 218.343 953.527 178.043 1005.077 178.043ZM661.637 43.933C716.857 43.933 762.777 91.553 762.777 146.773C762.777 201.993 718.547 247.913 661.637 247.913C606.417 247.913 560.497 200.303 560.497 145.083C560.497 89.853 606.417 43.933 661.637 43.933Z" />
            <path id="role_2"
              d="M574.017 1152.443L171.347 1286.563L405.017 974.923L574.017 1152.443ZM598.687 1107.643L174.757 662.433L651.727 524.613L598.687 1107.643ZM950.317 796.563L648.607 1098.283V1098.003L698.797 545.023L950.317 796.563ZM960.527 737.863L713.827 491.153L960.527 244.443V737.863ZM632.717 478.963L153.767 617.343V0.003L632.717 478.963ZM805.987 329.793L679.517 456.263L541.697 318.453L674.697 4.823L805.987 329.793ZM1136.357 277.053L1192.777 446.623L1009.317 410.043L1009.317 222.603L1136.357 277.053Z" />
            <path id="role_3"
              d="M660.087 0.283C1010.477 0.283 1298.207 288.023 1298.207 638.413C1298.207 988.803 1010.477 1276.543 660.087 1276.543C309.707 1276.543 21.967 989.083 21.967 638.413C21.967 542.883 46.347 452.163 84.337 370.803L199.147 485.613C183.267 533.523 174.197 565.643 174.197 638.413C174.197 906.593 392.197 1124.593 660.377 1124.593C928.557 1124.593 1146.557 906.593 1146.557 638.413C1146.557 370.233 928.557 152.233 660.377 152.233C607.087 152.233 555.777 161.303 507.577 177.173L392.767 62.363C474.137 24.383 564.567 0.003 660.377 0.003L660.087 0.283ZM660.087 301.903C845.487 301.903 996.587 453.003 996.587 638.413C996.587 823.813 845.487 974.913 660.087 974.913C474.677 974.913 323.587 824.093 323.587 638.413C323.587 598.723 331.807 561.303 344.277 526.153H389.347L473.267 609.573C473.267 609.573 473.267 535.223 473.267 638.413C473.267 741.593 517.177 825.513 660.377 825.513C703.567 825.513 787.487 741.593 787.487 638.413C787.487 535.223 703.567 451.303 660.377 451.303C651.587 451.303 643.087 452.723 634.587 453.853L548.117 367.393V322.323C583.557 309.853 620.977 301.623 660.377 301.623L660.087 301.903ZM197.157 2.833C211.337 -2.837 227.207 0.283 237.977 11.053L462.217 237.843C469.307 244.653 473.267 254.293 473.267 264.203V398.293L686.747 611.763C687.307 612.043 687.317 612.603 687.317 612.613H687.027C701.207 627.353 701.487 650.603 685.897 665.343C671.157 679.513 649.047 679.233 634.587 665.343C634.307 664.783 633.757 664.773 633.537 664.773L420.257 451.303H286.177C276.257 451.303 266.897 447.343 259.817 440.253L33.027 213.463C22.257 202.693 19.137 186.533 24.807 172.643C30.477 158.753 44.367 149.673 59.397 149.673H174.197V37.423C174.197 22.403 183.267 8.503 197.157 2.833Z" />
          </g>

          <g v-if="adavanceRender">
            <mask id="mask0_669_3527" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="1318"
              height="1318">
              <path class="circle" fill-rule="evenodd" clip-rule="evenodd"
                d="M658.987 2.53779e-07C745.528 -0.00240136 831.223 17.0409 911.178 50.1572C991.133 83.2736 1063.78 131.815 1124.98 193.008C1186.17 254.201 1234.71 326.849 1267.83 406.803C1300.95 486.756 1318 572.451 1318 658.992C1318 789.33 1279.35 916.741 1206.94 1025.11C1134.53 1133.49 1031.61 1217.95 911.194 1267.83C790.778 1317.71 658.276 1330.76 530.443 1305.34C402.61 1279.91 285.187 1217.15 193.023 1124.99C100.859 1032.83 38.094 915.405 12.6645 787.572C-12.7648 659.74 0.283843 527.238 50.1596 406.821C100.036 286.404 184.499 183.481 292.87 111.067C401.24 38.6541 528.65 0.00243245 658.987 2.53779e-07ZM841.376 218.679C754.29 182.607 658.461 173.17 566.011 191.56C473.561 209.949 388.641 255.34 321.988 321.993C255.335 388.646 209.943 473.567 191.553 566.018C173.163 658.468 182.602 754.295 218.673 841.382C254.745 928.468 315.83 1002.9 394.206 1055.27C472.581 1107.64 564.725 1135.6 658.987 1135.6C721.576 1135.6 783.552 1123.27 841.377 1099.32C899.203 1075.37 951.744 1040.26 996.001 996.008C1040.26 951.751 1075.37 899.209 1099.32 841.384C1123.27 783.558 1135.59 721.581 1135.59 658.992C1135.59 564.731 1107.64 472.587 1055.27 394.212C1002.9 315.837 928.463 254.751 841.376 218.679ZM1317.97 2.53779e-07V300.161C1248.75 173.415 1144.56 69.2272 1017.81 2.53779e-07H1317.97Z"
                fill="#B11425" />
            </mask>

            <g v-show="theme === 'dark'">
              <g mask="url(#mask0_669_3527)">
                <circle cx="944.5" cy="1255.5" r="900" fill="url(#grad1)" />
                <circle cx="161" cy="1233" r="800" fill="url(#grad2)" />
                <circle cx="1267" cy="788" r="800" fill="url(#grad3)" />
                <circle cx="96" cy="381" r="1000" fill="url(#grad4)" />
              </g>

              <defs>
                <radialGradient id="grad1">
                  <stop offset="0%" class="stop1" stop-color="#704A50" stop-opacity="0.8" />
                  <stop offset="100%" class="stop1" stop-color="#704A50" stop-opacity="0" />
                </radialGradient>

                <radialGradient id="grad2">
                  <stop offset="0%" class="stop2" stop-color="#351E24" stop-opacity="0.9" />
                  <stop offset="100%" class="stop2" stop-color="#351E24" stop-opacity="0" />
                </radialGradient>

                <radialGradient id="grad3">
                  <stop offset="0%" class="stop3" stop-color="#62000A" stop-opacity="0.8" />
                  <stop offset="100%" class="stop3" stop-color="#62000A" stop-opacity="0" />
                </radialGradient>

                <radialGradient id="grad4">
                  <stop offset="0%" class="stop4" stop-color="#2F151E" stop-opacity="0.9" />
                  <stop offset="100%" class="stop4" stop-color="#2F151E" stop-opacity="0" />
                </radialGradient>
              </defs>
            </g>

            <g v-show="theme === 'light'">
              <g mask="url(#mask0_669_3527)">
                <circle cx="944.5" cy="1255.5" r="900" fill="url(#grad2_1)" />
                <circle cx="161" cy="1233" r="800" fill="url(#grad2_2)" />
                <circle cx="1267" cy="788" r="800" fill="url(#grad2_3)" />
                <circle cx="96" cy="381" r="1000" fill="url(#grad2_4)" />
              </g>

              <defs>
                <radialGradient id="grad2_1">
                  <stop offset="0%" class="stop2_1" stop-color="#CB938C" stop-opacity="0.8" />
                  <stop offset="100%" class="stop2_1" stop-color="#CB938C" stop-opacity="0" />
                </radialGradient>

                <radialGradient id="grad2_2">
                  <stop offset="0%" class="stop2_2" stop-color="#943D45" stop-opacity="0.9" />
                  <stop offset="100%" class="stop2_2" stop-color="#943D45" stop-opacity="0" />
                </radialGradient>

                <radialGradient id="grad2_3">
                  <stop offset="0%" class="stop2_3" stop-color="#B92E3D" stop-opacity="0.8" />
                  <stop offset="100%" class="stop2_3" stop-color="#B92E3D" stop-opacity="0" />
                </radialGradient>

                <radialGradient id="grad2_4">
                  <stop offset="0%" class="stop2_4" stop-color="#DABCBA" stop-opacity="0.9" />
                  <stop offset="100%" class="stop2_4" stop-color="#DABCBA" stop-opacity="0" />
                </radialGradient>
              </defs>
            </g>
          </g>

        </svg>
      </div>
    </div>

  </div>
</template>

<style lang="scss" scoped>
.circle-move-effect {
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0px;
  top: 0px;
  display: flex;
  justify-content: center;
  padding-top: 100px;
  pointer-events: none;

  .content-wrapper {
    opacity: 0;
    width: 1318px;
    height: 1318px;
    will-change: transform;
    transform: translateZ(0);
  }

  .content {
    svg {
      width: 100%;
      height: 100%;
    }
  }
}

#app {
  &.index {
    .circle-move-effect {
      padding-top: 120px;
      .content-wrapper .content {
        transform: translateY(var(--centerHeight)) scale(0.243);
        transform-origin: top center;
      }
    }
  }


  &.service {
    .circle-move-effect {
      align-items: start;

      .content-wrapper {
        width: 1360px;
        height: 1360px;
      }

      .content {
        transform: translate(30%, 430px) !important;
      }
    }
  }

  &.about {
    .circle-move-effect {
      align-items: center;

      .content-wrapper {
        width: 625px;
        height: 625px;

        .content {
          transform: translate(0%, -50px) !important;
        }
      }
    }
  }
}

#app {
  &.xs {
    &.index {
      .circle-move-effect {
        height: 130%;
        overflow: hidden;
        padding-top: 100px;
        .content-wrapper {
          width: 473px;
          height: 473px;
          flex-shrink: 0;

          .content {
            // transform: translate(0%, 20px);
            transform: translateY(var(--centerHeight)) scale(0.3168);
            transform-origin: top center;
          }
        }
      }
    }

    &.about {
      .circle-move-effect {
        align-items: start;

        .content-wrapper {
          width: 553.502px;
          height: 553.502px;

          .content {
            transform: translate(0%, 50px) scale(1) !important;
          }
        }
      }
    }


    &.service {
      .circle-move-effect {
        .content-wrapper {
          width: 380px;
          height: 380px;
        }

        .content {
          transform: translate(14%, 16px) !important;
        }
      }
    }

  }

}
</style>